import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import type {
  ApiResponse,
  SessionVideosResponse,
  VideoPlaybackInfo,
  VideoResolutionsInfo,
  VideoDownloadInfo,
  VideoProcessingStatus,
  VideoDeleteResult,
  MultipartUploadInitiation,
  PresignedUploadPart,
  CompleteMultipartUploadInput,
  MultipartUploadCompletion,
  SessionUsageInfo,
} from "@/types";

export function createUploadsApi({ client }: HttpClient) {
  return {
    async getSessionVideos(
      sessionId: string,
    ): Promise<SessionVideosResponse> {
      const response: AxiosResponse<ApiResponse<SessionVideosResponse>> =
        await client.get(`/uploads/videos/session/${sessionId}`);
      return response.data.data;
    },

    async getVideoPlaybackUrl(
      videoId: string,
      resolution?: string,
    ): Promise<VideoPlaybackInfo> {
      const url = resolution
        ? `/uploads/videos/playback/${videoId}?resolution=${encodeURIComponent(resolution)}`
        : `/uploads/videos/playback/${videoId}`;

      const response: AxiosResponse<ApiResponse<VideoPlaybackInfo>> =
        await client.get(url);
      return response.data.data;
    },

    async getVideoResolutions(videoId: string): Promise<VideoResolutionsInfo> {
      const response: AxiosResponse<ApiResponse<VideoResolutionsInfo>> =
        await client.get(`/uploads/videos/${videoId}/resolutions`);
      return response.data.data;
    },

    async getVideoPlaybackUrlWithResolution(
      videoId: string,
      resolution: string,
    ): Promise<VideoPlaybackInfo> {
      const response: AxiosResponse<ApiResponse<VideoPlaybackInfo>> =
        await client.get(
          `/uploads/videos/playback/${videoId}/${encodeURIComponent(resolution)}`,
        );
      return response.data.data;
    },

    async getVideoDownloadUrl(videoId: string): Promise<VideoDownloadInfo> {
      const response: AxiosResponse<ApiResponse<VideoDownloadInfo>> =
        await client.get(`/uploads/videos/download/${videoId}`);
      return response.data.data;
    },

    async getVideoProcessingStatus(
      videoId: string,
    ): Promise<VideoProcessingStatus> {
      const response: AxiosResponse<ApiResponse<VideoProcessingStatus>> =
        await client.get(`/uploads/videos/${videoId}/processing-status`);
      return response.data.data;
    },

    async deleteVideo(videoId: string): Promise<VideoDeleteResult> {
      const response: AxiosResponse<ApiResponse<VideoDeleteResult>> =
        await client.delete(`/uploads/videos/${videoId}`);
      return response.data.data;
    },

    async initiateMultipartUpload(input: {
      filename: string;
      contentType: string;
      fileSize: number;
      sessionId: string;
    }): Promise<MultipartUploadInitiation> {
      const response: AxiosResponse<ApiResponse<MultipartUploadInitiation>> =
        await client.post("/uploads/videos/initiate", input);
      return response.data.data;
    },

    async getPresignedPartUrl(input: {
      uploadId: string;
      key: string;
      partNumber: number;
    }): Promise<PresignedUploadPart> {
      const response: AxiosResponse<ApiResponse<PresignedUploadPart>> =
        await client.post("/uploads/videos/presign-part", input);
      return response.data.data;
    },

    async completeMultipartUpload(
      input: CompleteMultipartUploadInput,
    ): Promise<MultipartUploadCompletion> {
      const response: AxiosResponse<ApiResponse<MultipartUploadCompletion>> =
        await client.post("/uploads/videos/complete", input);
      return response.data.data;
    },

    async abortMultipartUpload(input: {
      uploadId: string;
      key: string;
    }): Promise<void> {
      await client.post("/uploads/videos/abort", input);
    },

    async getSessionUsage(sessionId: string): Promise<SessionUsageInfo> {
      const response: AxiosResponse<ApiResponse<SessionUsageInfo>> =
        await client.get(`/uploads/videos/session/${sessionId}/usage`);
      return response.data.data;
    },
  };
}
